import classNames from 'classnames';
import React, { useMemo } from 'react';

export interface AvatarProps {
  src?: string;
  size?: 'sm' | 'smMd' | 'md' | 'lg' | 'full';
  className?: string;
  classNameLetters?: string;
  firstName?: string;
  lastName?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  withBorder?: boolean;
  withRounded?: boolean;
  colorLetters?: 'white' | 'black' | 'blue';
  colorBackground?: 'blue' | 'black' | 'transparent';
  checked?: boolean;
}

export const Avatar = ({
  className,
  classNameLetters,
  src,
  size = 'md',
  onClick,
  lastName = '',
  firstName = '',
  withBorder = false,
  withRounded = true,
  colorLetters,
  colorBackground,
  checked,
}: AvatarProps) => {
  const letters = useMemo(
    () =>
      firstName && !lastName
        ? firstName
        : `${firstName ? firstName?.charAt(0).trim() || firstName?.charAt(1).trim() : ''}${
            lastName ? lastName?.charAt(0).trim() || lastName?.charAt(1).trim() : ''
          }`
            .trim()
            .toUpperCase(),
    [firstName, lastName],
  );

  return (
    <div
      className={classNames(
        'overflow-hidden flex justify-center items-center text-neutral-400 bg-neutral-200 hover:scale-110 transition-all relative',
        {
          'w-6 h-6 min-w-[24px] min-h-[24px]':
            size == 'sm' && !className?.includes(' w-') && !className?.includes(' h-'),
          'w-8 h-8 min-w-[32px] min-h-[32px]':
            size == 'smMd' && !className?.includes(' w-') && !className?.includes(' h-'),
          'w-9 h-9 min-w-[36px] min-h-[36px]':
            size == 'md' && !className?.includes(' w-') && !className?.includes(' h-'),
          'w-[128px] h-[128px] min-w-[128px] min-h-[128px]':
            size === 'lg' && !className?.includes(' w-') && !className?.includes(' h-'),
          'w-full h-full': size === 'full',
          border: withBorder,
          'rounded-full': withRounded,
          'bg-gradient-to-r from-blue-500 to-blue-200': !src && colorBackground === 'blue',
          'bg-[#C7D3FC]': !src && colorBackground === 'black',
          'bg-opacity-25 bg-white': !src && colorBackground === 'transparent',
          'hover:cursor-pointer': !!onClick,
        },
        className,
      )}
      onClick={onClick}
    >
      {src ? (
        <img
          src={src}
          alt={`Avatar ${firstName || ''} ${lastName || ''}`}
          title={`${firstName || ''} ${lastName || ''}`}
          className="object-cover h-full w-full"
        />
      ) : letters ? (
        <span
          className={classNames(
            'flex items-center justify-center select-none font-bold text-neutral-400 bg-neutral-200 leading-[22px] text-nowrap',
            {
              'text-white': colorLetters === 'white',
              'text-black': colorLetters === 'black',
              'text-[#818CF8]': colorLetters === 'blue',
              'text-[16px]':
                (size === 'sm' || size === 'smMd' || size === 'md') &&
                !classNameLetters?.includes('text-'),
              'text-7xl': size === 'lg' && !classNameLetters?.includes('text-'),
              'text-center text-[16px]': size === 'full',
              'hover:cursor-pointer': !!onClick,
            },
            classNameLetters,
          )}
          title={`${firstName} ${lastName}`}
        >
          {letters}
        </span>
      ) : (
        <img src={''} alt="Avatar" title={`${firstName || ''} ${lastName || ''}`} />
      )}
      {checked === true && (
        <>
          <span
            className={classNames(
              'bg-[#6366F1] absolute bg-opacity-[0.72]',
              {
                'w-6 h-6':
                  size == 'sm' && !className?.includes(' w-') && !className?.includes(' h-'),
                'w-8 h-8':
                  size == 'smMd' && !className?.includes(' w-') && !className?.includes(' h-'),
                'w-9 h-9':
                  size == 'md' && !className?.includes(' w-') && !className?.includes(' h-'),
                'w-32 h-32':
                  size === 'lg' && !className?.includes(' w-') && !className?.includes(' h-'),
                'w-full h-full': size === 'full',
              },
              className,
            )}
          ></span>
          <span className="absolute left-[20%] top-[25%]">
            <img src={''} alt="checked" className="h-5 w-5 max-h-[20px] max-w-[20px]" />
          </span>
        </>
      )}
    </div>
  );
};
